export const GUIDELINES_TYPES = {
  PHOTO: 'PHOTO',
  VIDEO: 'VIDEO',
  WRITE: 'WRITE',
} as const;

export const FIELD_TYPES = {
  scale: 'scale',
  size: 'size',
  tiles: 'tiles',
} as const;

export const PHOTO_LIMITS = {
  PHOTO_SIZE_LIMIT: 5,
  MAX_PHOTOS_COUNT: 3,
  MIN_PHOTO_WIDTH: 100,
  MIN_PHOTO_HEIGHT: 100,
} as const;

export const VIDEO_LIMITS = {
  VIDEO_SIZE_LIMIT: 50,
  MAX_VIDEOS_COUNT: 1,
} as const;

export const ONE_MB = 1048576;

export const ACCEPTABLE_VIDEO_FORMATS = ['3gp', '3g2', 'asf', 'avi', 'dv', 'flv', 'mov', 'mp4', 'f4v', 'mpeg', 'mpg', 'qt', 'wmv'];
export const ACCEPTABLE_VIDEO_FORMATS_INPUT = '.3gp, .3g2, .asf, .avi, .dv, .flv, .mov, .mp4, .f4v, .mpeg, .mpg, .qt, .wmv';

export const ACCEPTABLE_VIDEO_FORMATS_UI = 'GP/3G2, ASF, AVI, DV, FLV, MOV, MP4, F4V, MPEG, MPG, QT or WMV';

const extraQuestionsNames = {
  overallFitAllClothing: 'overallFitAllClothing',
  overallFit: 'overallFit',
  size: 'size',
  sizeWithHeight: 'sizeWithHeight',
  whyILikeItBakeware: 'whyILikeItBakeware',
  whyILikeItCutleryAndKnives: 'whyILikeItCutleryAndKnives',
  whyILikeItEyeMakeup: 'whyILikeItEyeMakeup',
  whyILikeItFoodAndBeverage: 'whyILikeItFoodAndBeverage',
  whyILikeItKitchenElectrics: 'whyILikeItKitchenElectrics',
  whyILikeItLips: 'whyILikeItLips',
  whyILikeItTabletopAndGlassware: 'whyILikeItTabletopAndGlassware',
  warmthCwa: 'warmthCwa',
  warmthCoats: 'warmthCoats',
  fabric: 'fabric',
  coverage: 'coverage',
  skinType: 'skinType',
  hairType: 'hairType',
  bestForHairCare: 'bestForHairCare',
  mattressFeel: 'mattressFeel',
  scent: 'scent',
  shoeFit: 'shoeFit',
  bestForSkinCare: 'bestForSkinCare',
} as const;

export const sizeNames = {
  sizeOrdered: 'sizeOrdered',
  usualSize: 'usualSize',
  height: 'height',
} as const;

export const extraQuestionsList = {
  [extraQuestionsNames.overallFit]: {
    name: extraQuestionsNames.overallFit,
    title: 'Overall Fit',
    type: FIELD_TYPES.scale,
    options: [
      { label: 'Runs small', value: 1, id: `${extraQuestionsNames.overallFit}-runsSmall` },
      { label: 'True to size', value: 2, id: `${extraQuestionsNames.overallFit}-trueToSize` },
      { label: 'Runs big', value: 3, id: `${extraQuestionsNames.overallFit}-runsBig` },
    ],
  },
  // Intentionally duplicating the previous one because we need to fit data structure from BV
  [extraQuestionsNames.overallFitAllClothing]: {
    name: extraQuestionsNames.overallFitAllClothing,
    title: 'Overall Fit',
    type: FIELD_TYPES.scale,
    options: [
      { label: 'Runs small', value: 1, id: `${extraQuestionsNames.overallFitAllClothing}-runsSmall` },
      { label: 'True to size', value: 2, id: `${extraQuestionsNames.overallFitAllClothing}-trueToSize` },
      { label: 'Runs big', value: 3, id: `${extraQuestionsNames.overallFitAllClothing}-runsBig` },
    ],
  },
  [extraQuestionsNames.size]: {
    name: extraQuestionsNames.size,
    title: 'Size',
    type: FIELD_TYPES.size,
    options: [
      { label: 'Size ordered', name: sizeNames.sizeOrdered, id: `${extraQuestionsNames.size}-sizeOrdered` },
      { label: 'Usual size', name: sizeNames.usualSize, id: `${extraQuestionsNames.size}-usualSize` },
    ],
  },
  [extraQuestionsNames.sizeWithHeight]: {
    name: extraQuestionsNames.size,
    title: 'Size',
    type: FIELD_TYPES.size,
    options: [
      { label: 'Size ordered', name: sizeNames.sizeOrdered, id: `${extraQuestionsNames.sizeWithHeight}-sizeOrdered` },
      { label: 'Usual size', name: sizeNames.usualSize, id: `${extraQuestionsNames.sizeWithHeight}-usualSize` },
      { label: 'Height', name: sizeNames.height, id: `${extraQuestionsNames.sizeWithHeight}-height` },
    ],
  },
  [extraQuestionsNames.whyILikeItBakeware]: {
    name: extraQuestionsNames.whyILikeItBakeware,
    title: 'Why I like it',
    type: FIELD_TYPES.tiles,
    options: [
      { text: 'Quality', id: 'tagid_LikeItBakeware-Quality' },
      { text: 'Size', id: 'tagid_LikeItBakeware-Size' },
      { text: 'Weight', id: 'tagid_LikeItBakeware-Weight' },
      { text: 'Durable', id: 'tagid_LikeItBakeware-Durable' },
      { text: 'Versatile', id: 'tagid_LikeItBakeware-Versatile' },
      { text: 'Easy to clean', id: 'tagid_LikeItBakeware-EasyToClean' },
      { text: 'Easy to use', id: 'tagid_LikeItBakeware-EasyToUse' },
    ],
  },
  [extraQuestionsNames.whyILikeItCutleryAndKnives]: {
    name: extraQuestionsNames.whyILikeItCutleryAndKnives,
    title: 'Why I like it',
    type: FIELD_TYPES.tiles,
    options: [
      { text: 'Quality', id: 'tagid_LikeItKnives-Quality' },
      { text: 'Size', id: 'tagid_LikeItKnives-Size' },
      { text: 'Weight', id: 'tagid_LikeItKnives-Weight' },
      { text: 'Sharp', id: 'tagid_LikeItKnives-Sharp' },
      { text: 'Durable', id: 'tagid_LikeItKnives-Durable' },
      { text: 'Balanced', id: 'tagid_LikeItKnives-Balanced' },
      { text: 'Versatile', id: 'tagid_LikeItKnives-Versatile' },
      { text: 'Easy to clean', id: 'tagid_LikeItKnives-EasyToClean' },
    ],
  },
  [extraQuestionsNames.whyILikeItEyeMakeup]: {
    name: extraQuestionsNames.whyILikeItEyeMakeup,
    title: 'Why I like it',
    type: FIELD_TYPES.tiles,
    options: [
      { text: 'Long-wearing', id: 'tagid_LikeItEyeMakeup-Longwearing' },
      { text: 'Easy to apply', id: 'tagid_LikeItEyeMakeup-EasyToApply' },
      { text: 'True to color', id: 'tagid_LikeItEyeMakeup-TrueToColor' },
    ],
  },
  [extraQuestionsNames.whyILikeItFoodAndBeverage]: {
    name: extraQuestionsNames.whyILikeItFoodAndBeverage,
    title: 'Why I like it',
    type: FIELD_TYPES.tiles,
    options: [
      { text: 'Flavor', id: 'tagid_LikeItFood-Flavor' },
      { text: 'Quality', id: 'tagid_LikeItFood-Quality' },
      { text: 'Great gift', id: 'tagid_LikeItFood-GreatGift' },
      { text: 'Decadent', id: 'tagid_LikeItFood-Decadent' },
      { text: 'Good for entertaining', id: 'tagid_LikeItFood-GoodForEntertaining' },
    ],
  },
  [extraQuestionsNames.whyILikeItKitchenElectrics]: {
    name: extraQuestionsNames.whyILikeItKitchenElectrics,
    title: 'Why I like it',
    type: FIELD_TYPES.tiles,
    options: [
      { text: 'Quality', id: 'tagid_LikeItKitchenelectrics-Quality' },
      { text: 'Size', id: 'tagid_LikeItKitchenelectrics-Size' },
      { text: 'Weight', id: 'tagid_LikeItKitchenelectrics-Weight' },
      { text: 'Durable', id: 'tagid_LikeItKitchenelectrics-Durable' },
      { text: 'Versatile', id: 'tagid_LikeItKitchenelectrics-Versatile' },
      { text: 'Quiet', id: 'tagid_LikeItKitchenelectrics-Quiet' },
      { text: 'Fast', id: 'tagid_LikeItKitchenelectrics-Fast' },
      { text: 'Powerful', id: 'tagid_LikeItKitchenelectrics-Powerful' },
      { text: 'Easy to use', id: 'tagid_LikeItKitchenelectrics-EasyToUse' },
      { text: 'Easy to clean', id: 'tagid_LikeItKitchenelectrics-EasyToClean' },
    ],
  },
  [extraQuestionsNames.whyILikeItLips]: {
    name: extraQuestionsNames.whyILikeItLips,
    title: 'Why I like it',
    type: FIELD_TYPES.tiles,
    options: [
      { text: 'Moisturizing', id: 'tagid_LikeItLips-Moisturizing' },
      { text: 'Long-wearing', id: 'tagid_LikeItLips-Longwearing' },
      { text: 'True to color', id: 'tagid_LikeItLips-TrueToColor' },
      { text: 'Contains SPF', id: 'tagid_LikeItLips-ContainsSpf' },
    ],
  },
  [extraQuestionsNames.whyILikeItTabletopAndGlassware]: {
    name: extraQuestionsNames.whyILikeItTabletopAndGlassware,
    title: 'Why I like it',
    type: FIELD_TYPES.tiles,
    options: [
      { text: 'Quality', id: 'tagid_LikeItTabletop_1-Quality' },
      { text: 'Size', id: 'tagid_LikeItTabletop_1-Size' },
      { text: 'Versatile', id: 'tagid_LikeItTabletop_1-Versatile' },
      { text: 'Easy to clean', id: 'tagid_LikeItTabletop_1-EasyToClean' },
      { text: 'Good for entertaining', id: 'tagid_LikeItTabletop_1-GoodForEntertaining' },
      { text: 'Durable', id: 'tagid_LikeItTabletop_1-Durable' },
    ],
  },
  [extraQuestionsNames.warmthCwa]: {
    name: extraQuestionsNames.warmthCwa,
    title: 'Warmth',
    type: FIELD_TYPES.scale,
    options: [
      { label: 'Lightweight', value: 1, id: `${extraQuestionsNames.warmthCwa}-lightweight` },
      { label: 'Average', value: 2, id: `${extraQuestionsNames.warmthCwa}-average` },
      { label: 'Very warm', value: 3, id: `${extraQuestionsNames.warmthCwa}-veryWarm` },
    ],
  },
  // Intentionally duplicating the previous one because we need to fit data structure from BV
  [extraQuestionsNames.warmthCoats]: {
    name: extraQuestionsNames.warmthCoats,
    title: 'Warmth',
    type: FIELD_TYPES.scale,
    options: [
      { label: 'Lightweight', value: 1, id: `${extraQuestionsNames.warmthCoats}-lightweight` },
      { label: 'Average', value: 2, id: `${extraQuestionsNames.warmthCoats}-average` },
      { label: 'Very warm', value: 3, id: `${extraQuestionsNames.warmthCoats}-veryWarm` },
    ],
  },
  [extraQuestionsNames.fabric]: {
    name: extraQuestionsNames.fabric,
    title: 'Fabric',
    type: FIELD_TYPES.scale,
    options: [
      { label: 'Rigid', value: 1, id: `${extraQuestionsNames.fabric}-rigid` },
      { label: 'Average', value: 2, id: `${extraQuestionsNames.fabric}-average` },
      { label: 'Stretchy', value: 3, id: `${extraQuestionsNames.fabric}-stretchy` },
    ],
  },
  [extraQuestionsNames.coverage]: {
    name: extraQuestionsNames.coverage,
    title: 'Coverage',
    type: FIELD_TYPES.scale,
    options: [
      { label: 'Sheer', value: 1, id: `${extraQuestionsNames.coverage}-sheer` },
      { label: 'Average', value: 2, id: `${extraQuestionsNames.coverage}-average` },
      { label: 'Full', value: 3, id: `${extraQuestionsNames.coverage}-full` },
    ],
  },
  [extraQuestionsNames.skinType]: {
    name: extraQuestionsNames.skinType,
    title: 'My skin type is',
    type: FIELD_TYPES.tiles,
    options: [
      { text: 'Dry', id: 'tagid_SkinType-Dry' },
      { text: 'Combination', id: 'tagid_SkinType-Combination' },
      { text: 'Oily', id: 'tagid_SkinType-Oily' },
      { text: 'Sensitive', id: 'tagid_SkinType-Sensitive' },
      { text: 'Acne-prone', id: 'tagid_SkinType-Acneprone' },
    ],
  },
  [extraQuestionsNames.hairType]: {
    name: extraQuestionsNames.hairType,
    title: 'Hair type',
    type: FIELD_TYPES.tiles,
    options: [
      { text: 'Straight', id: 'tagid_HairType-Straight' },
      { text: 'Wavy', id: 'tagid_HairType-Wavy' },
      { text: 'Curly', id: 'tagid_HairType-Curly' },
      { text: 'Fine', id: 'tagid_HairType-Fine' },
      { text: 'Thick', id: 'tagid_HairType-Thick' },
      { text: 'Dry', id: 'tagid_HairType-Dry' },
      { text: 'Oily', id: 'tagid_HairType-Oily' },
      { text: 'Damaged', id: 'tagid_HairType-Damaged' },
    ],
  },
  [extraQuestionsNames.bestForHairCare]: {
    name: extraQuestionsNames.bestForHairCare,
    title: 'Best for',
    type: FIELD_TYPES.tiles,
    options: [
      { text: 'Volume', id: 'tagid_BestForHair-Volume' },
      { text: 'Shine', id: 'tagid_BestForHair-Shine' },
      { text: 'Moisturizing', id: 'tagid_BestForHair-Moisturizing' },
      { text: 'Deep conditioning', id: 'tagid_BestForHair-DeepConditioning' },
      { text: 'Taming frizz', id: 'tagid_BestForHair-TamingFrizz' },
      { text: 'Smoothing', id: 'tagid_BestForHair-Smoothing' },
      { text: 'Detangling', id: 'tagid_BestForHair-Detangling' },
      { text: 'Daily use', id: 'tagid_BestForHair-DailyUse' },
    ],
  },
  [extraQuestionsNames.mattressFeel]: {
    name: extraQuestionsNames.mattressFeel,
    title: 'Feel',
    type: FIELD_TYPES.scale,
    options: [
      { label: 'Soft', value: 1, id: `${extraQuestionsNames.mattressFeel}-soft` },
      { label: 'Average', value: 2, id: `${extraQuestionsNames.mattressFeel}-average` },
      { label: 'Firm', value: 3, id: `${extraQuestionsNames.mattressFeel}-firm` },
    ],
  },
  [extraQuestionsNames.scent]: {
    name: extraQuestionsNames.scent,
    title: 'Scent',
    type: FIELD_TYPES.scale,
    options: [
      { label: 'Light', value: 1, id: `${extraQuestionsNames.scent}-light` },
      { label: 'Average', value: 2, id: `${extraQuestionsNames.scent}-average` },
      { label: 'Intense', value: 3, id: `${extraQuestionsNames.scent}-intense` },
    ],
  },
  [extraQuestionsNames.shoeFit]: {
    name: extraQuestionsNames.shoeFit,
    title: 'Width',
    type: FIELD_TYPES.scale,
    options: [
      { label: 'Runs narrow', value: 1, id: `${extraQuestionsNames.shoeFit}-runsNarrow` },
      { label: 'Average', value: 2, id: `${extraQuestionsNames.shoeFit}-average` },
      { label: 'Runs wide', value: 3, id: `${extraQuestionsNames.shoeFit}-runsWide` },
    ],
  },
  [extraQuestionsNames.bestForSkinCare]: {
    name: extraQuestionsNames.bestForSkinCare,
    title: 'Best for',
    type: FIELD_TYPES.tiles,
    options: [
      { text: 'Moisturizing', id: 'tagid_BestForSkincare_1-Moisturizing' },
      { text: 'Anti-aging', id: 'tagid_BestForSkincare_1-Antiaging' },
      { text: 'Brightening', id: 'tagid_BestForSkincare_1-Brightening' },
      { text: 'Lifting/Firming', id: 'tagid_BestForSkincare_1-LiftingFirming' },
      { text: 'Exfoliating', id: 'tagid_BestForSkincare_1-Exfoliating' },
      { text: 'Dryness', id: 'tagid_BestForSkincare_1-Dryness' },
      { text: 'Redness', id: 'tagid_BestForSkincare_1-Redness' },
      { text: 'Large pores', id: 'tagid_BestForSkincare_1-LargePores' },
      { text: 'Fine lines and wrinkles', id: 'tagid_BestForSkincare_1-FineLinesAndWrinkles' },
      { text: 'Acne', id: 'tagid_BestForSkincare_1-Acne' },
      { text: 'Sun protection', id: 'tagid_BestForSkincare_1-SunProtection' },
    ],
  },
} as const;

export const categoryNames = {
  BV_ALLClothing: 'BV_ALLClothing',
  BV_Bakeware: 'BV_Bakeware',
  BV_Coats: 'BV_Coats',
  BV_CutleryKnives: 'BV_CutleryKnives',
  BV_CWAccessories: 'BV_CWAccessories',
  BV_Dresses: 'BV_Dresses',
  BV_EyeMakeup: 'BV_EyeMakeup',
  BV_FoodBeverage: 'BV_FoodBeverage',
  BV_FoundationConcealer: 'BV_FoundationConcealer',
  BV_HairCare: 'BV_HairCare',
  BV_KitchenElectrics: 'BV_KitchenElectrics',
  BV_Lips: 'BV_Lips',
  BV_MattressesPillows: 'BV_MattressesPillows',
  BV_PerfumesCandles: 'BV_PerfumesCandles',
  BV_Shoes: 'BV_Shoes',
  BV_Skincare: 'BV_Skincare',
  BV_TableTopGlassware: 'BV_TableTopGlassware',
} as const;

export const questionsForCategory = {
  [categoryNames.BV_ALLClothing]: [extraQuestionsNames.overallFitAllClothing, extraQuestionsNames.size],
  [categoryNames.BV_Bakeware]: [extraQuestionsNames.whyILikeItBakeware],
  [categoryNames.BV_Coats]: [extraQuestionsNames.overallFit, extraQuestionsNames.warmthCwa, extraQuestionsNames.size],
  [categoryNames.BV_CutleryKnives]: [extraQuestionsNames.whyILikeItCutleryAndKnives],
  [categoryNames.BV_CWAccessories]: [extraQuestionsNames.warmthCwa],
  [categoryNames.BV_Dresses]: [extraQuestionsNames.overallFit, extraQuestionsNames.fabric, extraQuestionsNames.sizeWithHeight],
  [categoryNames.BV_EyeMakeup]: [extraQuestionsNames.whyILikeItEyeMakeup],
  [categoryNames.BV_FoodBeverage]: [extraQuestionsNames.whyILikeItFoodAndBeverage],
  [categoryNames.BV_FoundationConcealer]: [extraQuestionsNames.coverage, extraQuestionsNames.skinType],
  [categoryNames.BV_HairCare]: [extraQuestionsNames.hairType, extraQuestionsNames.bestForHairCare],
  [categoryNames.BV_KitchenElectrics]: [extraQuestionsNames.whyILikeItKitchenElectrics],
  [categoryNames.BV_Lips]: [extraQuestionsNames.whyILikeItLips],
  [categoryNames.BV_MattressesPillows]: [extraQuestionsNames.mattressFeel],
  [categoryNames.BV_PerfumesCandles]: [extraQuestionsNames.scent],
  [categoryNames.BV_Shoes]: [extraQuestionsNames.overallFit, extraQuestionsNames.shoeFit, extraQuestionsNames.size],
  [categoryNames.BV_Skincare]: [extraQuestionsNames.skinType, extraQuestionsNames.bestForSkinCare],
  [categoryNames.BV_TableTopGlassware]: [extraQuestionsNames.whyILikeItTabletopAndGlassware],
} as const;

export const bvApiFieldsNamesMapper = {
  [extraQuestionsNames.overallFit]: 'rating_OverallFitRadio',
  [extraQuestionsNames.overallFitAllClothing]: 'rating_FitClothing/valueLabel1',
  [extraQuestionsNames.warmthCwa]: 'rating_WarmthCwa',
  [extraQuestionsNames.warmthCoats]: 'rating_WarmthCoats',
  [extraQuestionsNames.fabric]: 'rating_Fabric',
  [extraQuestionsNames.coverage]: 'rating_CoverageMakeup',
  [extraQuestionsNames.mattressFeel]: 'rating_MattressFeel',
  [extraQuestionsNames.scent]: 'rating_ScentCandlesperfume',
  [extraQuestionsNames.shoeFit]: 'rating_ShoeFitRadio',
  [sizeNames.sizeOrdered]: 'additionalfield_Sizeordered',
  [sizeNames.height]: 'additionalfield_Height',
  [sizeNames.usualSize]: 'additionalfield_UsualSize',
} as const;
